import React, { useState, useEffect } from "react";
import { useUser } from "../../hooks/useUser";
import { useUpdateProfile } from "../../hooks/useUserMutations";
import { maskCPF, maskPhone } from "../../utils/masks";

const PersonalInfo = () => {
  const { data: user, isLoading, isError } = useUser();
  const updateProfile = useUpdateProfile();
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  // Estado para os campos do formulário
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    cpf: "",
    birth_date: "",
    gender: "",
  });

  // Atualiza o estado quando os dados do usuário são carregados
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        cpf: user.cpf || "",
        birth_date: user.birth_date?.substring(0, 10) || "",
        gender: user.gender || "",
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const id = e.target.id || e.target.name; // pega id ou name
    let value = e.target.value;

    // Aplica máscaras conforme o campo
    let formattedValue = value;
    if (id === "cpf") formattedValue = maskCPF(value);
    if (id === "phone") formattedValue = maskPhone(value);

    setFormData((prev) => ({
      ...prev,
      [id]: formattedValue,
    }));

    if (errors[id]) {
      setErrors((prev) => ({ ...prev, [id]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Nome é obrigatório";
    if (!formData.email.trim()) {
      newErrors.email = "E-mail é obrigatório";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "E-mail inválido";
    }
    if (!formData.phone.trim()) newErrors.phone = "Telefone é obrigatório";
    if (formData.cpf && !/^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(formData.cpf)) {
      newErrors.cpf = "CPF inválido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Cria uma cópia do formData para alterar antes do envio
    const dataToSend = {
      ...formData,
      birth_date: formData.birth_date === "" ? null : formData.birth_date,
    };

    try {
      await updateProfile.mutateAsync(dataToSend);
      setSuccessMessage("Perfil atualizado com sucesso!");
      setIsEditing(false);
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      // O erro já é tratado no hook
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setSuccessMessage("");
    setErrors({});
  };

  const handleCancel = () => {
    setIsEditing(false);
    setErrors({});
    // Restaura os valores originais
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        cpf: user.cpf || "",
        birth_date: user.birth_date?.substring(0, 10) || "",
        gender: user.gender || "",
      });
    }
  };

  if (isLoading) return <div className="p-4">Carregando...</div>;
  if (isError)
    return (
      <div className="p-4 text-red-500">Erro ao carregar dados do usuário.</div>
    );

  return (
    <div className="space-y-4 pt-4 m-[2%]">
      <div className="rounded-lg border bg-white shadow-sm">
        <div className="p-6 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold">Informações Pessoais</h2>
            <p className="text-sm text-gray-500">
              {isEditing
                ? "Edite suas informações"
                : "Visualize suas informações"}
            </p>
          </div>

          {!isEditing && (
            <button
              onClick={handleEditClick}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
              Editar Perfil
            </button>
          )}
        </div>

        <div className="p-6 space-y-6">
          {successMessage && (
            <div className="p-4 bg-green-100 text-green-700 rounded-md">
              {successMessage}
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            {/* Nome */}
            <div className="space-y-2">
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700">
                Nome Completo
              </label>
              {isEditing ? (
                <>
                  <input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    className={`block w-full rounded-md shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border ${
                      errors.name ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500">{errors.name}</p>
                  )}
                </>
              ) : (
                <p className="text-gray-900">{user.name}</p>
              )}
            </div>

            {/* E-mail */}
            <div className="space-y-2">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700">
                E-mail
              </label>
              {isEditing ? (
                <>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`block w-full rounded-md shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border ${
                      errors.email ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.email && (
                    <p className="text-sm text-red-500">{errors.email}</p>
                  )}
                </>
              ) : (
                <p className="text-gray-900">{user.email}</p>
              )}
            </div>

            {/* Telefone */}
            <div className="space-y-2">
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700">
                Telefone
              </label>
              {isEditing ? (
                <>
                  <input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`block w-full rounded-md shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border ${
                      errors.phone ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="(00) 00000-0000"
                  />
                  {errors.phone && (
                    <p className="text-sm text-red-500">{errors.phone}</p>
                  )}
                </>
              ) : (
                <p className="text-gray-900">{user.phone || "Não informado"}</p>
              )}
            </div>

            {/* CPF */}
            <div className="space-y-2">
              <label
                htmlFor="cpf"
                className="block text-sm font-medium text-gray-700">
                CPF
              </label>
              {isEditing ? (
                <>
                  <input
                    id="cpf"
                    type="text"
                    value={formData.cpf}
                    onChange={handleChange}
                    className={`block w-full rounded-md shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border ${
                      errors.cpf ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="000.000.000-00"
                    maxLength="14"
                  />
                  {errors.cpf && (
                    <p className="text-sm text-red-500">{errors.cpf}</p>
                  )}
                </>
              ) : (
                <p className="text-gray-900">
                  {user.cpf ? maskCPF(user.cpf) : "Não informado"}
                </p>
              )}
            </div>

            {/* Data de Nascimento */}
            <div className="space-y-2">
              <label
                htmlFor="birth_date"
                className="block text-sm font-medium text-gray-700">
                Data de Nascimento
              </label>
              {isEditing ? (
                <input
                  id="birth_date"
                  type="date"
                  value={formData.birth_date}
                  onChange={handleChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border"
                />
              ) : (
                <p className="text-gray-900">
                  {user.birth_date
                    ? new Date(user.birth_date).toLocaleDateString("pt-BR")
                    : "Não informado"}
                </p>
              )}
            </div>

            {/* Gênero */}
            <div className="space-y-2">
              <label
                htmlFor="gender"
                className="block text-sm font-medium text-gray-700">
                Gênero
              </label>
              {isEditing ? (
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}>
                  <option value="">Selecione</option>
                  <option value="male">Masculino</option>
                  <option value="female">Feminino</option>
                  <option value="other">Outro</option>
                  <option value="prefer-not-to-say">
                    Prefiro não informar
                  </option>
                </select>
              ) : (
                <p className="text-gray-900">
                  {user.gender === "male"
                    ? "Masculino"
                    : formData.gender === "female"
                    ? "Feminino"
                    : formData.gender === "other"
                    ? "Outro"
                    : formData.gender === "prefer-not-to-say"
                    ? "Prefiro não informar"
                    : "Não informado"}
                </p>
              )}
            </div>
          </div>

          {isEditing && (
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors">
                Cancelar
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={updateProfile.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:bg-gray-400">
                {updateProfile.isPending ? "Salvando..." : "Salvar Alterações"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonalInfo;
